import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { X, Camera, FileText, Sparkles, PenLine, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

/**
 * LogFoodModal — unified entry modal with 4 tabs:
 *   label_photo  — snap nutrition label (Gemini Vision, exact)
 *   meal_photo   — snap plated meal   (Gemini Vision, estimated)
 *   quick_add    — type "chicken bowl, rice" (Gemini, estimated)
 *   manual       — type the numbers yourself
 */
const TABS = [
  { id: "label_photo", label: "Label", icon: FileText, desc: "Snap a nutrition label" },
  { id: "meal_photo", label: "Meal", icon: Camera, desc: "Snap your plate — estimate" },
  { id: "quick_add", label: "Type it", icon: Sparkles, desc: "'Chicken bowl, rice, broccoli'" },
  { id: "manual", label: "Manual", icon: PenLine, desc: "Enter exact numbers" },
];

export const LogFoodModal = ({ onClose, onLogged }) => {
  const [tab, setTab] = useState("label_photo");
  const [saving, setSaving] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="log-food-modal"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-surface border border-border rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-surface z-10">
          <h2 className="font-heading text-lg font-bold text-white">Log food</h2>
          <button onClick={onClose} className="text-text-muted hover:text-white" data-testid="log-food-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab picker */}
        <div className="grid grid-cols-4 border-b border-border">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                disabled={saving}
                className={`py-3 px-2 flex flex-col items-center gap-1 text-xs transition-colors ${
                  tab === t.id ? "bg-green-500/10 text-green-400 border-b-2 border-green-500" : "text-text-secondary hover:text-white"
                }`}
                data-testid={`log-tab-${t.id}`}
              >
                <Icon className="w-4 h-4" />
                <span className="font-bold">{t.label}</span>
              </button>
            );
          })}
        </div>

        <div className="p-5">
          <p className="text-xs text-text-muted mb-4">{TABS.find((t) => t.id === tab)?.desc}</p>

          {tab === "label_photo" && (
            <PhotoForm
              endpoint={`${API}/nutrition/food/scan-label`}
              cta="Scan label"
              includeServings
              saving={saving}
              setSaving={setSaving}
              onDone={onLogged}
            />
          )}
          {tab === "meal_photo" && (
            <PhotoForm
              endpoint={`${API}/nutrition/food/scan-meal`}
              cta="Estimate meal"
              saving={saving}
              setSaving={setSaving}
              onDone={onLogged}
              estimateNotice
            />
          )}
          {tab === "quick_add" && (
            <QuickAddForm saving={saving} setSaving={setSaving} onDone={onLogged} />
          )}
          {tab === "manual" && (
            <ManualForm saving={saving} setSaving={setSaving} onDone={onLogged} />
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// ====== PHOTO FORM ======
const PhotoForm = ({ endpoint, cta, includeServings, saving, setSaving, onDone, estimateNotice }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [servings, setServings] = useState("1");
  const inputRef = useRef(null);

  const onPick = (f) => {
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      return toast.error("Use a JPEG, PNG, or WEBP image");
    }
    if (f.size > 5 * 1024 * 1024) return toast.error("Image too large — max 5MB");
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!file) return toast.error("Pick an image first");
    setSaving(true);
    const fd = new FormData();
    fd.append("image", file);
    if (includeServings) fd.append("servings", servings || "1");
    try {
      const res = await fetch(endpoint, { method: "POST", credentials: "include", body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed");
      }
      const log = await res.json();
      toast.success(`Logged: ${log.name} (${Math.round(log.calories)} cal)`);
      onDone();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div
        onClick={() => !saving && inputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
          preview ? "border-green-500/50 bg-green-500/5" : "border-border hover:border-green-500/40"
        }`}
        data-testid="photo-drop-zone"
      >
        {preview ? (
          <img src={preview} alt="" className="max-h-40 rounded-lg mx-auto" />
        ) : (
          <>
            <Upload className="w-8 h-8 text-text-muted mx-auto mb-2" />
            <p className="text-sm text-white font-bold">Tap to take a photo</p>
            <p className="text-xs text-text-muted mt-1">or choose from gallery</p>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          onChange={(e) => onPick(e.target.files?.[0])}
          className="hidden"
          data-testid="photo-input"
        />
      </div>

      {includeServings && (
        <div>
          <label className="text-xs uppercase tracking-widest text-text-muted font-bold mb-1.5 block">
            Servings you ate
          </label>
          <input
            type="number"
            step="0.25"
            value={servings}
            onChange={(e) => setServings(e.target.value)}
            className="w-full bg-[#0B0F1A] border border-border rounded-lg px-3 py-2.5 text-white focus:border-green-500/70 focus:outline-none"
            data-testid="servings-input"
          />
        </div>
      )}

      {estimateNotice && (
        <p className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
          Heads up — plated-meal macros are AI estimates and can be off by 20–40%. Good enough for trends, not a diet plan.
        </p>
      )}

      <button
        onClick={submit}
        disabled={saving || !file}
        className="w-full bg-green-600 hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
        data-testid="photo-submit-btn"
      >
        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
        {saving ? "Analyzing…" : cta}
      </button>
    </div>
  );
};

// ====== QUICK ADD ======
const QuickAddForm = ({ saving, setSaving, onDone }) => {
  const [text, setText] = useState("");

  const submit = async () => {
    if (!text.trim()) return toast.error("Describe what you ate");
    setSaving(true);
    try {
      const res = await fetch(`${API}/nutrition/food/quick-add`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: text.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed");
      }
      const log = await res.json();
      toast.success(`Logged: ${log.name} (${Math.round(log.calories)} cal)`);
      onDone();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Chicken bowl with rice and broccoli, ~500g"
        rows={4}
        maxLength={500}
        className="w-full bg-[#0B0F1A] border border-border rounded-lg px-3 py-3 text-white placeholder-text-muted focus:border-green-500/70 focus:outline-none resize-none"
        data-testid="quick-add-input"
      />
      <p className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
        Rough estimate by AI. Good for speed, not precision.
      </p>
      <button
        onClick={submit}
        disabled={saving || !text.trim()}
        className="w-full bg-green-600 hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
        data-testid="quick-add-submit"
      >
        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
        {saving ? "Estimating…" : "Log it"}
      </button>
    </div>
  );
};

// ====== MANUAL ======
const ManualForm = ({ saving, setSaving, onDone }) => {
  const [form, setForm] = useState({
    name: "", servings: "1", calories: "", protein_g: "", carbs_g: "", fat_g: "",
  });

  const bind = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      const res = await fetch(`${API}/nutrition/food/manual`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          servings: Number(form.servings) || 1,
          calories: Number(form.calories) || 0,
          protein_g: Number(form.protein_g) || 0,
          carbs_g: Number(form.carbs_g) || 0,
          fat_g: Number(form.fat_g) || 0,
        }),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success("Logged");
      onDone();
    } catch (e) {
      toast.error("Couldn't log");
    } finally {
      setSaving(false);
    }
  };

  const field = (k, label, type = "number", step) => (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-text-muted font-bold mb-1 block">{label}</label>
      <input
        type={type}
        step={step}
        value={form[k]}
        onChange={bind(k)}
        className="w-full bg-[#0B0F1A] border border-border rounded-lg px-3 py-2 text-sm text-white focus:border-green-500/70 focus:outline-none"
        data-testid={`manual-${k}`}
      />
    </div>
  );

  return (
    <div className="space-y-3">
      {field("name", "Food name", "text")}
      <div className="grid grid-cols-2 gap-3">
        {field("servings", "Servings", "number", "0.25")}
        {field("calories", "Calories", "number")}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {field("protein_g", "Protein g")}
        {field("carbs_g", "Carbs g")}
        {field("fat_g", "Fat g")}
      </div>
      <button
        onClick={submit}
        disabled={saving}
        className="w-full bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white font-bold py-3 rounded-lg transition-colors"
        data-testid="manual-submit"
      >
        {saving ? "Saving…" : "Log it"}
      </button>
    </div>
  );
};

export default LogFoodModal;
